import React, { FormEvent, useEffect, useState } from "react";
import "./TicketSystem.css";
import TicketService from "../../../Services/Magasin/Ticket";
import axios from "axios";
import { NewSideBarAdmin } from "../../NewSideBarAdmin";

interface Conversation {
  sender: string;
  date: string;
  message: string;
}

interface TicketData {
  id: number;
  IdUtilisateur: number;
  NamUtilisateur: string;
  Probleme: string;
  Description: string;
  Status: "Lu" | "Non lu" | "Rejeté";
  Date: string;
  conversation?: Conversation[];
}

interface TicketApiState {
  Ticket: TicketData[];
  error: string;
}

type Tab = "admin" | "magasin_client" | "commercial";

const STATUS_CLASS: Record<TicketData["Status"], string> = {
  Lu: "status-Lu-tks",
  "Non lu": "status-NonLu-tks",
  Rejeté: "status-Rejete-tks",
};

const isSupportAdmin = (sender: string) =>
  sender === "Support Admin" || sender === "Admin";

const getInitials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("") || "?";

const TicketSystem: React.FC = () => {
  const [selectedTicket, setSelectedTicket] = useState<TicketData | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>("admin");
  const [loading, setLoading] = useState(true);

  const [state, setState] = useState<TicketApiState>({
    Ticket: [],
    error: "Aucune commande",
  });

  const [Message, setMessage] = useState("");
  const [conversation, setConversation] = useState<Conversation[]>([]);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [ShowErreurPopup, setShowErreurPopup] = useState(false);
  const [conversationLoading, setConversationLoading] = useState(false);

  // Fetch conversation when ticket changes
  useEffect(() => {
    if (selectedTicket) {
      setConversationLoading(true);

      axios
        .get(
          `${process.env.REACT_APP_PHP_APP_URL}/magasin/${selectedTicket.IdUtilisateur}/ticket/${selectedTicket.id}/answer`
        )
        .then((res) => {
          const answers: Conversation[] = res.data.map((ans: any) => ({
            sender: ans.NamUtilisateur,
            date: new Date(ans.Date).toLocaleString(),
            message: ans.Message,
          }));
          setConversation(answers);
          setConversationLoading(false);
        })
        .catch((err) => {
          console.error("Erreur lors du chargement des réponses :", err);
          setConversation([]);
          setConversationLoading(false);
        });
    }
  }, [selectedTicket]);

  // Send a new message
  const handleSubmitAswer = async (e: FormEvent) => {
    e.preventDefault();
    if (!selectedTicket) return;

    const formData = new FormData();
    formData.append("IdUtilisateur", selectedTicket.IdUtilisateur.toString());
    formData.append("IdTicket", selectedTicket.id.toString());
    formData.append("NamUtilisateur", "Support Admin");
    formData.append("Message", Message);

    try {
      await axios.post(
        `${process.env.REACT_APP_PHP_APP_URL}/magasin/${selectedTicket.IdUtilisateur}/answer/send`,
        formData
      );

      const res = await axios.get(
        `${process.env.REACT_APP_PHP_APP_URL}/magasin/${selectedTicket.IdUtilisateur}/ticket/${selectedTicket.id}/answer`
      );
      const answers: Conversation[] = res.data.map((ans: any) => ({
        sender: ans.NamUtilisateur,
        date: new Date(ans.Date).toLocaleString(),
        message: ans.Message,
      }));
      setConversation(answers);

      setMessage("");
      setShowSuccessPopup(true);
      setTimeout(() => setShowSuccessPopup(false), 2000);
    } catch (error) {
      setShowErreurPopup(true);
      setTimeout(() => setShowErreurPopup(false), 2000);
    }
  };

  // Fetch all tickets on mount
  useEffect(() => {
    setLoading(true);

    TicketService()
      .getTicket()
      .then((res) => {
        setState((prev) => ({
          ...prev,
          Ticket: res.data,
        }));
        setLoading(false);
      })
      .catch((error) => {
        setState((prev) => ({
          ...prev,
          error: error.message || "Erreur lors du chargement",
        }));
        setLoading(false);
      });
  }, []);

  // Filter tickets
  const filteredTickets = state.Ticket.filter((ticket) => {
    const matchesSearch =
      ticket.Probleme.toLowerCase().includes(search.toLowerCase()) ||
      ticket.Description.toLowerCase().includes(search.toLowerCase());

    const matchesStatus = statusFilter ? ticket.Status === statusFilter : true;

    const isClientMessage = ticket.Probleme.toLowerCase() === "message de client";
    const isCommercialMessage = ticket.Probleme.toLowerCase() === "message de commercial";

    let matchesTab = false;
    if (activeTab === "admin") {
      matchesTab = !isClientMessage && !isCommercialMessage;
    } else if (activeTab === "magasin_client") {
      matchesTab = isClientMessage;
    } else if (activeTab === "commercial") {
      matchesTab = isCommercialMessage;
    }

    return matchesSearch && matchesStatus && matchesTab;
  });

  const switchTab = (tab: Tab) => {
    setActiveTab(tab);
    setSelectedTicket(null);
  };

  return (
    <>
      <NewSideBarAdmin />

      {loading ? (
        <div className="ticket-container-tks">
          <div className="ticket-list-tks">
            <h2 className="list-title-tks">
              <i className="bi bi-headset" />
              Gestion des Tickets
            </h2>
            <p className="list-subtitle-tks">Suivez et répondez aux demandes en cours</p>
            <div className="skeleton-tabs-tks shimmer-tks" />
            <div className="skeleton-filterbox-tks shimmer-tks" />
            <div className="ticket-scroll-tks">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="skeleton-card-tks shimmer-tks" style={{ animationDelay: `${i * 0.05}s` }} />
              ))}
            </div>
          </div>
          <div className="ticket-detail-tks">
            <div className="skeleton-header-tks shimmer-tks" />
            <div className="skeleton-conversation-tks shimmer-tks" />
          </div>
        </div>
      ) : (
        <div className="ticket-container-tks">
          <button
            className="menu-toggle-tks"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Ouvrir la liste des tickets"
          >
            <i className="bi bi-list" />
          </button>

          {isMenuOpen && (
            <div className="overlay-tks" onClick={() => setIsMenuOpen(false)} />
          )}

          <div className={`ticket-list-tks ${isMenuOpen ? "open-tks" : ""}`}>
            <h2 className="list-title-tks">
              <i className="bi bi-headset" />
              Gestion des Tickets
            </h2>
            <p className="list-subtitle-tks">
              Suivez et répondez aux demandes en cours
            </p>

            <div className="tabs-tks">
              <div
                className={`tab-indicator-tks ${
                  activeTab === "magasin_client" ? "shift-tks" : activeTab === "commercial" ? "shift-tks-2" : ""
                }`}
              />
              <button
                className={`tab-btn-tks ${activeTab === "admin" ? "active-tks" : ""}`}
                onClick={() => switchTab("admin")}
              >
                Tickets Magasins
              </button>
              <button
                className={`tab-btn-tks ${
                  activeTab === "magasin_client" ? "active-tks" : ""
                }`}
                onClick={() => switchTab("magasin_client")}
              >
                Tickets Clients
              </button>
              <button
                className={`tab-btn-tks ${
                  activeTab === "commercial" ? "active-tks" : ""
                }`}
                onClick={() => switchTab("commercial")}
              >
                Tickets Commerciaux
              </button>
            </div>

            <div className="filter-box-tks">
              <div className="search-wrapper-tks">
                <i className="bi bi-search" />
                <input
                  type="text"
                  placeholder="Rechercher un ticket..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="search-input-tks"
                />
              </div>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="status-filter-tks"
              >
                <option value="">Tous les statuts</option>
                <option value="Lu">Lu</option>
                <option value="Non lu">Non lu</option>
                <option value="Rejeté">Rejeté</option>
              </select>
            </div>

            <div className="ticket-scroll-tks">
              {filteredTickets.length > 0 ? (
                filteredTickets.map((ticket, index) => (
                  <div
                    key={ticket.id}
                    className={`ticket-card-tks ${
                      selectedTicket?.id === ticket.id ? "active-tks" : ""
                    }`}
                    style={{ animationDelay: `${Math.min(index, 10) * 0.04}s` }}
                    onClick={() => {
                      setSelectedTicket(ticket);
                      setIsMenuOpen(false);
                    }}
                  >
                    <div className="ticket-header-tks">
                      <span className={`status-tks ${STATUS_CLASS[ticket.Status]}`}>
                        {ticket.Status}
                      </span>
                      <span className="creator-tks">
                        Créé par {ticket.NamUtilisateur}
                      </span>
                    </div>
                    <h3>{ticket.Probleme}</h3>
                    <p>{ticket.Description}</p>
                    <small>Créé le {ticket.Date}</small>
                  </div>
                ))
              ) : (
                <div className="empty-list-tks">
                  <i className="bi bi-inbox" />
                  <p>Aucun ticket trouvé.</p>
                </div>
              )}
            </div>
          </div>

          <div className="ticket-detail-tks">
            {selectedTicket ? (
              <>
                <div className="detail-header-tks">
                  <div className="detail-info-tks">
                    <h2>
                      <span className="id-badge-tks">#{selectedTicket.id}</span> {selectedTicket.Probleme}
                    </h2>
                    <div className="sender-badge-tks">
                      <i className="bi bi-person-fill" /> {selectedTicket.NamUtilisateur}
                    </div>
                  </div>
                  <div className="detail-actions-tks">
                    <span className={`status-tks ${STATUS_CLASS[selectedTicket.Status]}`}>
                      {selectedTicket.Status}
                    </span>
                  </div>
                </div>

                <div className="conversation-area-tks">
                  {conversationLoading ? (
                    <div className="conversation-loading-tks">
                      <div className="skeleton-bubble-tks shimmer-tks" />
                      <div className="skeleton-bubble-tks shimmer-tks self-tks" />
                    </div>
                  ) : conversation.length > 0 ? (
                    conversation.map((msg, index) => {
                      const self = isSupportAdmin(msg.sender);
                      return (
                        <div
                          key={index}
                          className={`message-row-tks ${self ? "self-tks" : ""}`}
                          style={{ animationDelay: `${Math.min(index, 12) * 0.05}s` }}
                        >
                          <div className="avatar-tks">
                            {getInitials(self ? "Support Admin" : msg.sender)}
                          </div>
                          <div className="message-bubble-tks">
                            <div className="sender-row-tks">
                              <strong>{self ? "Support Admin" : msg.sender}</strong>
                              <small>{msg.date}</small>
                            </div>
                            <p>{msg.message}</p>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="empty-chat-tks">
                      <i className="bi bi-chat-square-text" />
                      <p>Aucun message pour ce ticket.</p>
                    </div>
                  )}
                </div>

                <div className="reply-wrapper-tks">
                  {activeTab === "admin" ? (
                    <form onSubmit={handleSubmitAswer} className="reply-box-tks">
                      <input
                        type="text"
                        value={Message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Tapez votre réponse..."
                        required
                      />
                      <button type="submit">
                        Envoyer <i className="bi bi-send-fill" />
                      </button>
                    </form>
                  ) : (
                    <p className="readonly-note-tks">
                      <i className="bi bi-eye-fill" /> Vous ne pouvez que visualiser les tickets entre les magasins et
                      leurs {activeTab === "commercial" ? "commerciaux" : "clients"}.
                    </p>
                  )}
                </div>
              </>
            ) : (
              <div className="no-selection-tks">
                <i className="bi bi-info-circle" />
                <span>Sélectionnez un ticket pour voir les détails</span>
              </div>
            )}
          </div>

          {showSuccessPopup && (
            <div className="popup-tks success-tks" role="status">
              <i className="bi bi-check-circle-fill" /> Message envoyé !
            </div>
          )}
          {ShowErreurPopup && (
            <div className="popup-tks error-tks" role="alert">
              <i className="bi bi-x-circle-fill" /> Erreur lors de l'envoi
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default TicketSystem;